import React, { useEffect, useState } from "react";
import { collection, query, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";

interface DashboardProps {
  eventId: string;
}

const Dashboard: React.FC<DashboardProps> = ({ eventId }) => {
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [maxTickets, setMaxTickets] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) {
      setTotalRegistrations(0);
      setMaxTickets(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    const fetchEventDetails = async () => {
      const eventDoc = await getDoc(doc(db, "events", eventId));
      if (eventDoc.exists()) {
        setMaxTickets(eventDoc.data().maxTickets || 0);
      }
    };

    fetchEventDetails();

    const q = query(collection(db, `events/${eventId}/registrations`));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setTotalRegistrations(querySnapshot.size);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [eventId]);

  const availableTickets = Math.max(0, maxTickets - totalRegistrations);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-primary text-text p-6">
          <h2 className="text-2xl font-bold">Event Statistics</h2>
        </div>
        <div className="p-6 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-primary text-text p-6">
        <h2 className="text-2xl font-bold">Event Statistics</h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-4xl font-bold text-primary">
              {totalRegistrations}
            </p>
            <p className="text-gray-600 mt-2">Total Registrations</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-4xl font-bold text-green-600">
              {availableTickets}
            </p>
            <p className="text-gray-600 mt-2">Available Tickets</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-4xl font-semibold text-blue-600">{maxTickets}</p>
            <p className="text-gray-600 mt-2">Maximum Tickets</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Registration Progress</span>
            <span>{Math.round((totalRegistrations / maxTickets) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary h-2.5 rounded-full"
              style={{
                width: `${(totalRegistrations / maxTickets) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
